import { useState } from 'preact/hooks';
import { supabase } from '@/lib/supabase';
import { profile } from '@/state/data';
import { setLocation, evaluate } from '@/lib/theme';
import { subscribePush } from '@/lib/push';
import { Icon } from '@/components/Icon';

/** First-run, 3 steps: name -> notifications -> sunset. (onboarding.html) */
export function OnboardingScreen({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const saveName = async () => {
    const v = name.trim();
    if (v) {
      await supabase.from('profile').update({ display_name: v }).eq('onerow', true);
      if (profile.value) profile.value = { ...profile.value, display_name: v };
    }
    setStep(1);
  };

  const askNotifications = async () => {
    setBusy(true);
    await subscribePush().catch(() => undefined);
    setBusy(false);
    setStep(2);
  };

  const askLocation = () => {
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setLocation(pos.coords.latitude, pos.coords.longitude);
        await supabase
          .from('profile')
          .update({ lat: pos.coords.latitude, lng: pos.coords.longitude })
          .eq('onerow', true);
        evaluate(false);
        finish();
      },
      () => finish(),
      { timeout: 8000 }
    );
  };

  const finish = () => {
    localStorage.setItem('tend.onboarded', '1');
    onDone();
  };

  const steps = [
    {
      icon: 'paw' as const,
      title: 'Welcome to Tend',
      body: "I keep every feeding on the clock so you don't have to. First — what should I call you?",
      content: (
        <input
          class="input"
          placeholder="Your first name"
          aria-label="Your first name"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => e.key === 'Enter' && saveName()}
        />
      ),
      cta: 'Next',
      action: saveName,
      skip: () => setStep(1),
    },
    {
      icon: 'bell' as const,
      title: 'Feeding alarms',
      body: 'When a feeding comes due, I send a notification — even when the app is closed. Tap Allow on the next prompt.',
      content: null,
      cta: busy ? 'Asking…' : 'Turn on alarms',
      action: askNotifications,
      skip: () => setStep(2),
    },
    {
      icon: 'moon' as const,
      title: 'Dark at dusk',
      body: 'With your location I flip to dark mode at sunset — easier on your eyes (and the patients) during night feeds.',
      content: null,
      cta: busy ? 'Locating…' : 'Use my location',
      action: askLocation,
      skip: finish,
    },
  ];

  const s = steps[step];

  return (
    <div class="screen screen--no-tabbar" style={{ justifyContent: 'center' }}>
      <div style={{ padding: 'var(--s-6)', maxWidth: '340px', width: '100%', margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
        <div class="avatar avatar--xl" style={{ margin: '0 auto', background: 'var(--brand-primary)' }}>
          <Icon name={s.icon} size={30} />
        </div>
        <h1 class="t-h1" style={{ margin: 0 }}>{s.title}</h1>
        <p class="t-small" style={{ margin: 0 }}>{s.body}</p>
        {s.content}
        <button class="btn btn--lg btn--block" disabled={busy} onClick={s.action}>
          {s.cta}
        </button>
        <button class="btn btn--ghost" onClick={s.skip}>Skip for now</button>
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: 'var(--s-2)' }} aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === step ? '20px' : '6px',
                height: '6px',
                borderRadius: '999px',
                background: i === step ? 'var(--brand-primary)' : 'var(--stroke)',
                transition: 'width 260ms var(--ease-out-expo)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
